import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { Helmet } from 'react-helmet-async';

import PageHeader from './PageHeader';
import { SITE_URL } from '../../lib/seo';
import { tObject } from '../../lib/i18nSafe';

// Order matches the Sovereign AI thesis: six high-trust verticals.
const SECTOR_IDS = ['government', 'justice', 'healthcare', 'finance', 'logistics', 'energy'];

export default function Sectors() {
  const { t } = useTranslation();

  return (
    <Box>
      <Helmet>
        <title>Sectors | Al-Amen Technology</title>
        <link rel="canonical" href={`${SITE_URL}/sectors`} />
      </Helmet>
      <PageHeader
        eyebrow={t('sectors.eyebrow')}
        title={t('sectors.title')}
        body={t('sectors.body')}
      />

      <Container maxWidth="xl" sx={{ py: { xs: 5, md: 8 } }}>
        <Stack spacing={3}>
          {SECTOR_IDS.map((id, idx) => {
            const s = tObject(t, `sectorsData.${id}`);
            return (
              <Paper
                key={id}
                variant="outlined"
                id={id}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  bgcolor: 'background.paper',
                  scrollMarginTop: 96
                }}
              >
                <Grid container spacing={4}>
                  <Grid item xs={12} md={4}>
                    <Typography
                      sx={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: '0.82rem',
                        color: 'text.secondary'
                      }}
                    >
                      {String(idx + 1).padStart(2, '0')} · {t('sectors.blueprintEyebrow')}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'var(--font-serif)',
                        fontSize: { xs: '2rem', md: '2.4rem' },
                        letterSpacing: '-0.02em',
                        lineHeight: 1.05,
                        mt: 1.5,
                        mb: 1
                      }}
                    >
                      {s.title}
                    </Typography>
                    <Typography
                      sx={{
                        color: 'primary.main',
                        fontFamily: 'var(--font-mono)',
                        fontSize: '0.85rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em'
                      }}
                    >
                      {s.tagline}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Typography sx={{ color: 'text.secondary', mb: 3, fontSize: '1.02rem', lineHeight: 1.6 }}>
                      {s.body}
                    </Typography>

                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography
                          sx={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: '0.68rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.12em',
                            color: 'text.secondary',
                            mb: 1.25
                          }}
                        >
                          {t('sectors.stackLabel')}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                          {(s.stack || []).map((item) => (
                            <Chip
                              key={item}
                              label={item}
                              size="small"
                              variant="outlined"
                              sx={{ fontFamily: 'var(--font-mono)' }}
                            />
                          ))}
                        </Stack>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <Typography
                          sx={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: '0.68rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.12em',
                            color: 'text.secondary',
                            mb: 1.25
                          }}
                        >
                          {t('sectors.regulatorsLabel')}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                          {(s.regulators || []).map((item) => (
                            <Chip key={item} label={item} size="small" variant="outlined" />
                          ))}
                        </Stack>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Typography
                          sx={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: '0.68rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.12em',
                            color: 'text.secondary',
                            mb: 1.25
                          }}
                        >
                          {t('sectors.outcomesLabel')}
                        </Typography>
                        <Stack spacing={1}>
                          {(s.outcomes || []).map((o) => (
                            <Typography key={o} variant="body2" sx={{ color: 'text.primary' }}>
                              — {o}
                            </Typography>
                          ))}
                        </Stack>
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    <Button
                      component={RouterLink}
                      to="/contact"
                      variant="text"
                      endIcon={<ArrowForwardRoundedIcon />}
                      sx={{ color: 'text.primary' }}
                    >
                      {t('sectors.ctaLabel')}
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            );
          })}
        </Stack>
      </Container>
    </Box>
  );
}
